import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiTrash2, FiEye, FiAlertTriangle, FiMail, FiCornerUpLeft, FiInbox,
} from 'react-icons/fi';
import './Reclamation.css';

const API = process.env.REACT_APP_API_URL;

const TableReclamation = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEtat, setFilterEtat] = useState('all');
  const [selected, setSelected] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);

  const authorization = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${authorization}` };
  const navigate = useNavigate();

  const handleAuthError = (err) => {
    if ([401, 403, 404].includes(err.response?.status)) {
      localStorage.clear();
      navigate('/login');
    }
  };

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API}/contact/lister`, { headers })
      .then((res) => setMessages(Array.isArray(res.data) ? res.data : []))
      .catch((err) => handleAuthError(err))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, []);

  const filtered = useMemo(() => {
    let arr = [...messages];
    if (filterEtat === 'non_lu') arr = arr.filter((m) => !m.lu);
    else if (filterEtat === 'lu') arr = arr.filter((m) => m.lu);
    if (search.trim()) {
      const s = search.toLowerCase();
      arr = arr.filter((m) => m.email?.toLowerCase().includes(s) || m.sujet?.toLowerCase().includes(s) || m.message?.toLowerCase().includes(s));
    }
    arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return arr;
  }, [messages, filterEtat, search]);

  const nbNonLu = messages.filter((m) => !m.lu).length;
  const nbLu = messages.filter((m) => m.lu).length;

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleSelectAll = () => setSelected(selected.length === filtered.length ? [] : filtered.map((m) => m._id));

  const setLu = async (id, lu) => {
    try {
      await axios.post(`${API}/contact/${id}/lu`, { lu }, { headers });
      setMessages((prev) => prev.map((m) => (m._id === id ? { ...m, lu } : m)));
    } catch (err) {
      handleAuthError(err);
    }
  };

  const handleView = (msg) => {
    setViewTarget(msg);
    if (!msg.lu) setLu(msg._id, true);
  };

  const handleToggleEtat = (msg, e) => {
    e.stopPropagation();
    setLu(msg._id, !msg.lu);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.get(`${API}/contact/${deleteTarget._id}/delete`, { headers });
      setMessages((prev) => prev.filter((m) => m._id !== deleteTarget._id));
      if (viewTarget?._id === deleteTarget._id) setViewTarget(null);
      setDeleteTarget(null);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Supprimer ${selected.length} message(s) ?`)) return;
    try {
      await Promise.all(selected.map((id) => axios.get(`${API}/contact/${id}/delete`, { headers })));
      setMessages((prev) => prev.filter((m) => !selected.includes(m._id)));
      setSelected([]);
    } catch (err) {
      handleAuthError(err);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const initiales = (msg) => (msg.email ? msg.email.slice(0, 2).toUpperCase() : '?');

  return (
    <div className="amsg-page">
      <div className="amsg-header">
        <div>
          <h1 className="amsg-title">Réclamations</h1>
          <p className="amsg-subtitle">{loading ? 'Chargement…' : `${messages.length} message${messages.length > 1 ? 's' : ''} au total`}</p>
        </div>
        <button className="amsg-btn-refresh" onClick={fetchData}>Rafraîchir</button>
      </div>

      {!loading && (
        <div className="amsg-stats">
          <div className="amsg-stat amsg-stat--red">
            <div className="amsg-stat-icon"><FiMail size={18} /></div>
            <div>
              <p className="amsg-stat-num">{nbNonLu}</p>
              <p className="amsg-stat-label">Non lus</p>
            </div>
          </div>
          <div className="amsg-stat amsg-stat--green">
            <div className="amsg-stat-icon"><FiEye size={18} /></div>
            <div>
              <p className="amsg-stat-num">{nbLu}</p>
              <p className="amsg-stat-label">Lus</p>
            </div>
          </div>
          <div className="amsg-stat amsg-stat--blue">
            <div className="amsg-stat-icon"><FiInbox size={18} /></div>
            <div>
              <p className="amsg-stat-num">{messages.length}</p>
              <p className="amsg-stat-label">Total</p>
            </div>
          </div>
        </div>
      )}

      <div className="amsg-toolbar">
        <label className="amsg-search">
          <FiSearch size={14} />
          <input type="text" placeholder="Rechercher par email, sujet…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="amsg-search-clear" onClick={() => setSearch('')}>✕</button>}
        </label>

        <div className="amsg-pills">
          {[{ val: 'all', label: 'Tous' }, { val: 'non_lu', label: 'Non lus' }, { val: 'lu', label: 'Lus' }].map((f) => (
            <button key={f.val} className={`amsg-pill ${filterEtat === f.val ? 'amsg-pill--active' : ''}`} onClick={() => setFilterEtat(f.val)}>
              {f.label}
              {f.val === 'non_lu' && nbNonLu > 0 && <span className="amsg-pill-badge">{nbNonLu}</span>}
            </button>
          ))}
        </div>

        {selected.length > 0 && (
          <button className="amsg-bulk-delete" onClick={handleBulkDelete}>
            <FiTrash2 size={13} />
            Supprimer ({selected.length})
          </button>
        )}
      </div>

      <div className={`amsg-layout ${viewTarget ? 'amsg-layout--split' : ''}`}>
        <div className="amsg-table-wrap">
          <table className="amsg-table">
            <thead>
              <tr>
                <th className="amsg-th-check">
                  <input type="checkbox" checked={filtered.length > 0 && selected.length === filtered.length} onChange={toggleSelectAll} />
                </th>
                <th>Expéditeur</th>
                <th>Sujet</th>
                <th>État</th>
                <th>Date</th>
                <th className="amsg-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => <tr key={i}><td colSpan={6}><div className="amsg-skeleton" /></td></tr>)
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="amsg-empty">
                    <FiInbox size={36} />
                    <p>Aucun message trouvé</p>
                  </td>
                </tr>
              ) : (
                filtered.map((msg) => (
                  <tr
                    key={msg._id}
                    className={[!msg.lu ? 'amsg-row--unread' : '', selected.includes(msg._id) ? 'amsg-row--selected' : '', viewTarget?._id === msg._id ? 'amsg-row--active' : ''].join(' ')}
                    onClick={() => handleView(msg)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="amsg-th-check" onClick={(e) => { e.stopPropagation(); toggleSelect(msg._id); }}>
                      <input type="checkbox" checked={selected.includes(msg._id)} onChange={() => {}} />
                    </td>
                    <td>
                      <div className="amsg-sender">
                        <div className={`amsg-avatar ${!msg.lu ? 'amsg-avatar--unread' : ''}`}>{initiales(msg)}</div>
                        <div className="amsg-sender-info">
                          <span className="amsg-sender-name">{msg.email}</span>
                        </div>
                      </div>
                    </td>
                    <td><span className={`amsg-sujet ${!msg.lu ? 'amsg-sujet--bold' : ''}`}>{msg.sujet}</span></td>
                    <td>
                      <button className={`amsg-etat-btn amsg-etat-btn--${msg.lu ? 'lu' : 'non_lu'}`} onClick={(e) => handleToggleEtat(msg, e)} title="Cliquer pour changer l'état">
                        {!msg.lu ? '● Non lu' : '✓ Lu'}
                      </button>
                    </td>
                    <td className="amsg-date">{formatDate(msg.createdAt)}</td>
                    <td className="amsg-th-actions" onClick={(e) => e.stopPropagation()}>
                      <div className="amsg-actions">
                        <button className="amsg-action-btn amsg-action-btn--view" onClick={() => handleView(msg)} title="Voir"><FiEye size={14} /></button>
                        <button className="amsg-action-btn amsg-action-btn--delete" onClick={() => setDeleteTarget(msg)} title="Supprimer"><FiTrash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {viewTarget && (
          <div className="amsg-detail">
            <div className="amsg-detail-header">
              <div className="amsg-detail-avatar">{initiales(viewTarget)}</div>
              <div className="amsg-detail-sender">
                <p className="amsg-detail-name">{viewTarget.email}</p>
              </div>
              <button className="amsg-detail-close" onClick={() => setViewTarget(null)}>✕</button>
            </div>

            <div className="amsg-detail-meta">
              <span className="amsg-detail-sujet">{viewTarget.sujet}</span>
              <span className="amsg-detail-date">{formatDate(viewTarget.createdAt)}</span>
            </div>

            <div className="amsg-detail-body">
              <p>{viewTarget.message}</p>
            </div>

            <div className="amsg-detail-footer">
              <a href={`mailto:${viewTarget.email}?subject=Re: ${viewTarget.sujet}`} className="amsg-detail-reply">
                <FiCornerUpLeft size={14} />
                Répondre par email
              </a>
              <button className="amsg-detail-delete" onClick={() => { setDeleteTarget(viewTarget); setViewTarget(null); }}>Supprimer</button>
            </div>
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="amsg-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="amsg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="amsg-modal-icon"><FiAlertTriangle size={30} /></div>
            <h3 className="amsg-modal-title">Supprimer ce message ?</h3>
            <p className="amsg-modal-text">Le message de <strong>{deleteTarget.email}</strong> sera supprimé définitivement.</p>
            <div className="amsg-modal-actions">
              <button className="amsg-modal-btn amsg-modal-btn--cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</button>
              <button className="amsg-modal-btn amsg-modal-btn--confirm" onClick={handleDelete} disabled={deleting}>{deleting ? 'Suppression…' : 'Supprimer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableReclamation;
